import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Gift, Phone, User, MessageCircle, Check, AlertTriangle, Pause } from 'lucide-react';
import { toast } from 'sonner';

const SorteoDetailPage = () => {
  const { sorteoId } = useParams();
  const navigate = useNavigate();
  const [sorteo, setSorteo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedNumber, setSelectedNumber] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [tappedEntry, setTappedEntry] = useState(null);
  const [statusFilter, setStatusFilter] = useState('available');
  const [participantData, setParticipantData] = useState({
    name: '',
    phone: ''
  });

  useEffect(() => {
    fetchSorteo();
  }, [sorteoId]);

  const fetchSorteo = async () => {
    try {
      const response = await axios.get(`${API}/sorteos/${sorteoId}`);
      setSorteo(response.data);
    } catch (error) {
      console.error('Error fetching sorteo:', error);
      toast.error('Error al cargar el sorteo');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectNumber = (entry) => {
    if (entry.status !== 'available') {
      // Mostrar nombre del participante al tocar un número ocupado
      if (entry.participant_name) {
        setTappedEntry(entry);
        setTimeout(() => setTappedEntry(null), 3000);
      }
      return;
    }
    setSelectedNumber(entry);
    setShowForm(true);
  };

  const handleCancel = () => {
    setSelectedNumber(null);
    setShowForm(false);
    setParticipantData({ name: '', phone: '' });
  };

  const handleSubmit = async () => {
    if (!participantData.name.trim() || !participantData.phone.trim()) {
      toast.error('Completá tu nombre y teléfono');
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/sorteos/${sorteoId}/participate`, {
        sorteo_id: sorteoId,
        number: selectedNumber.number,
        participant_name: participantData.name,
        participant_phone: participantData.phone
      });

      setSuccess(true);
      toast.success('¡Ya estás participando!');

    } catch (error) {
      console.error('Error participating:', error);
      const errorMsg = error.response?.data?.detail || 'Error al participar';
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'available':
        return 'bg-green-500 hover:bg-green-600 text-white cursor-pointer';
      case 'reserved':
        return 'bg-gray-400 text-white cursor-not-allowed';
      default:
        return 'bg-gray-300';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-600 via-emerald-500 to-teal-500 flex items-center justify-center">
        <div className="text-white text-2xl">Cargando sorteo...</div>
      </div>
    );
  }

  if (!sorteo) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-600 via-emerald-500 to-teal-500 flex items-center justify-center">
        <div className="text-white text-2xl">Sorteo no encontrado</div>
      </div>
    );
  }

  if (!sorteo.is_active) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-600 via-emerald-500 to-teal-500 flex items-center justify-center p-4">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <Pause size={64} className="mx-auto mb-4 text-yellow-500" />
            <h2 className="text-2xl font-bold mb-2">Sorteo Pausado</h2>
            <p className="text-gray-600">Este sorteo no está disponible en este momento</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-600 via-emerald-500 to-teal-500 flex items-center justify-center p-4">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check size={40} className="text-white" />
            </div>
            <h2 className="text-2xl font-bold mb-2">¡Ya estás participando!</h2>
            <p className="text-gray-600 mb-4">
              Tu numero es: <span className="font-bold text-2xl text-green-600">{selectedNumber.number}</span>
              {selectedNumber.display_numbers && (
                <span className="block text-sm text-gray-500 mt-1">
                  Numeros: {selectedNumber.display_numbers.join(' - ')}
                </span>
              )}
            </p>
            <p className="text-sm text-gray-500 mb-4">
              Se abrió WhatsApp para que confirmes tu participación
            </p>
            <Button onClick={() => navigate('/')} className="bg-green-600 hover:bg-green-700">
              Volver al inicio
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const availableCount = sorteo.entries.filter(e => e.status === 'available').length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-600 via-emerald-500 to-teal-500 p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        <Button
          variant="outline"
          onClick={() => navigate(-1)}
          className="mb-6 bg-white/10 text-white hover:bg-white/20 border-white/30"
        >
          <ArrowLeft className="mr-2" size={20} />
          Volver
        </Button>

        {/* Header del sorteo */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-start gap-4">
              {sorteo.logo && (
                <img src={sorteo.logo} alt={sorteo.title} className="w-24 h-24 object-contain" />
              )}
              <div className="flex-1">
                <CardTitle className="text-3xl flex items-center gap-2">
                  <Gift className="text-green-600" />
                  {sorteo.title}
                </CardTitle>
                <Badge className="mt-2 bg-green-600">SORTEO GRATIS</Badge>
                <p className="text-gray-600 mt-2">
                  Elegí UN número y participá. Solo 1 número por persona.
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {sorteo.observations && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                <h3 className="font-semibold mb-1 flex items-center gap-2 text-amber-800">
                  📝 Observaciones
                </h3>
                <p className="text-gray-700 whitespace-pre-wrap text-sm" data-testid="sorteo-observations">
                  {sorteo.observations}
                </p>
              </div>
            )}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-lg mb-2">🏆 Premios:</h3>
                <ul className="space-y-1">
                  {sorteo.prizes.map((prize, idx) => (
                    <li key={idx} className="flex items-center gap-2">
                      <span className="text-green-600">•</span>
                      {prize}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <p className="text-lg">
                    <span className="font-bold text-green-600">{availableCount}</span>
                    <span className="text-gray-600"> números disponibles de </span>
                    <span className="font-bold">{sorteo.total_numbers}</span>
                  </p>
                </div>
              </div>
            </div>

            {/* Imágenes de premios */}
            {sorteo.prize_images?.length > 0 && (
              <div className="mt-4">
                <div className="flex gap-2 overflow-x-auto">
                  {sorteo.prize_images.map((img, idx) => (
                    <img
                      key={idx}
                      src={img}
                      alt={`Premio ${idx + 1}`}
                      className="w-32 h-32 object-cover rounded-lg"
                    />
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Formulario o Grilla */}
        {showForm ? (
          <Card>
            <CardHeader>
              <CardTitle>Completá tus datos</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-center mb-4">
                <p className="text-gray-600">Número elegido:</p>
                <Badge className="text-2xl px-4 py-2 bg-green-600">{selectedNumber.number}</Badge>
              </div>

              <div>
                <Label htmlFor="name">Tu nombre *</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <Input
                    id="name"
                    value={participantData.name}
                    onChange={(e) => setParticipantData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Juan Pérez"
                    className="pl-10"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="phone">Tu teléfono *</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <Input
                    id="phone"
                    value={participantData.phone}
                    onChange={(e) => setParticipantData(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="+54 9 11 1234-5678"
                    className="pl-10"
                  />
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  Solo podés participar 1 vez
                </p>
              </div>

              <div className="flex gap-4 pt-4">
                <Button variant="outline" onClick={handleCancel} className="flex-1">
                  Cancelar
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  {submitting ? (
                    'Procesando...'
                  ) : (
                    <>
                      <MessageCircle className="mr-2" size={20} />
                      Participar
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle>Números del sorteo</CardTitle>
              {/* Filtros */}
              <div className="flex flex-col sm:flex-row gap-2 mt-3">
                {[
                  { value: 'available', label: 'Disponibles', color: 'bg-green-500', activeText: 'text-white', count: sorteo.entries.filter(e => e.status === 'available').length },
                  { value: 'occupied', label: 'Ocupados', color: 'bg-gray-500', activeText: 'text-white', count: sorteo.entries.filter(e => e.status !== 'available').length },
                ].map(f => (
                  <button
                    key={f.value}
                    onClick={() => setStatusFilter(f.value)}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all w-full sm:w-auto ${
                      statusFilter === f.value
                        ? `${f.color} ${f.activeText} shadow-md`
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                    data-testid={`filter-${f.value}`}
                  >
                    <div className={`w-3 h-3 rounded-full ${statusFilter === f.value ? 'bg-white/50' : f.color}`}></div>
                    {f.label} ({f.count})
                  </button>
                ))}
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {/* Lineas filtradas (entries with display_numbers) */}
                {sorteo.entries
                  .filter(e => (statusFilter === 'available' ? e.status === 'available' : e.status !== 'available') && e.display_numbers && e.display_numbers.length > 0)
                  .map((entry) => {
                    const statusClass = entry.status === 'available'
                      ? 'bg-green-500 hover:bg-green-600 text-white cursor-pointer'
                      : 'bg-gray-400 text-white cursor-not-allowed';
                    return (
                      <div key={entry.id} className="relative">
                        <button
                          onClick={() => handleSelectNumber(entry)}
                          className={`${statusClass} w-full p-3 rounded-lg font-semibold text-sm flex items-center justify-between transition-all`}
                          data-testid={`sorteo-entry-${entry.number}`}
                        >
                          <span className="font-bold">{entry.number}</span>
                          <span className="text-xs opacity-90 ml-2">{entry.display_numbers.join(' - ')}</span>
                          {entry.participant_name && <span className="text-xs ml-2">{entry.participant_name}</span>}
                        </button>
                        {tappedEntry?.id === entry.id && entry.participant_name && (
                          <div className="absolute z-50 -top-8 left-1/2 -translate-x-1/2 px-3 py-1 bg-gray-900 text-white text-sm rounded shadow-lg whitespace-nowrap">
                            {entry.participant_name}
                          </div>
                        )}
                      </div>
                    );
                  })}

                {/* Numeros individuales en grilla */}
                {sorteo.entries.some(e => (statusFilter === 'available' ? e.status === 'available' : e.status !== 'available') && !e.display_numbers) && (
                  <div className="grid grid-cols-5 xs:grid-cols-6 sm:grid-cols-10 md:grid-cols-15 lg:grid-cols-20 gap-1.5 max-h-[500px] overflow-y-auto p-2">
                    {sorteo.entries
                      .filter(e => (statusFilter === 'available' ? e.status === 'available' : e.status !== 'available') && !e.display_numbers)
                      .map((entry) => (
                      <div key={entry.id} className="relative">
                        <button
                          onClick={() => handleSelectNumber(entry)}
                          className={`
                            w-full p-2 text-xs sm:text-sm font-mono rounded transition-all min-w-[40px] aspect-square flex items-center justify-center
                            ${getStatusColor(entry.status)}
                          `}
                          data-testid={`sorteo-entry-${entry.number}`}
                        >
                          {entry.number}
                        </button>
                        {tappedEntry?.id === entry.id && entry.participant_name && (
                          <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-gray-900 text-white text-xs rounded shadow-lg whitespace-nowrap"
                            data-testid={`tooltip-${entry.number}`}
                          >
                            {entry.participant_name}
                            <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {sorteo.entries.filter(e => statusFilter === 'available' ? e.status === 'available' : e.status !== 'available').length === 0 && (
                  <p className="text-center text-gray-500 py-8">
                    No hay numeros {statusFilter === 'available' ? 'disponibles' : 'ocupados'}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default SorteoDetailPage;
